import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Wallet } from "lucide-react";

export default function SuperAdminWallets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/wallets");
      setRows(data.items || []);
    } catch (e) { toast.error("Couldn't load wallets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const total = rows.reduce((s, r) => s + (r.mtd_spend || 0), 0);
  const low = rows.filter((r) => r.is_low).length;

  return (
    <div className="space-y-6" data-testid="superadmin-wallets-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="overline mb-1">Revenue</div>
          <h1 className="font-display text-3xl md:text-4xl">Tenant Wallets & Costs</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
            Per-tenant prepaid balances + month-to-date spend across OTP, AI Try-on, and Smart Catalog.
            Click any row to drill in and adjust.
          </p>
        </div>
        <Link to="/superadmin/pricing" className="kb-btn-outline" data-testid="link-pricing">Edit service pricing →</Link>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-md">
        <Stat label="Tenants" value={rows.length} />
        <Stat label="MTD revenue" value={`₹${total.toFixed(0)}`} tone="ok" />
        <Stat label="Low balance" value={low} tone={low > 0 ? "bad" : "muted"} />
      </div>

      <section className="bg-white border border-[hsl(var(--kb-border))]">
        <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] flex items-center justify-between">
          <div className="font-medium">All tenants</div>
          <button type="button" onClick={refresh} className="text-xs underline text-[hsl(var(--kb-muted))]" data-testid="wallets-refresh">
            <RefreshCw size={12} className="inline mr-1" /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-[hsl(var(--kb-muted))]">Loading…</div>
        ) : !rows.length ? (
          <div className="p-10 text-center text-sm text-[hsl(var(--kb-muted))]">No tenants yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="wallets-table">
            <thead className="bg-[hsl(var(--kb-surface-alt))]">
              <tr>
                <th className="p-3 text-left">Tenant</th>
                <th className="p-3 text-left">Slug</th>
                <th className="p-3 text-right">Balance</th>
                <th className="p-3 text-right">MTD spend</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tenant_id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`wallet-row-${r.tenant_id}`}>
                  <td className="p-3">
                    <Link to={`/superadmin/wallets/${r.tenant_id}`} className="hover:underline" data-testid={`wallet-link-${r.tenant_id}`}>
                      {r.name}
                    </Link>
                  </td>
                  <td className="p-3 font-mono text-xs">{r.slug}</td>
                  <td className={`p-3 text-right ${r.is_low ? "text-amber-700 font-medium" : ""}`}>
                    ₹{r.balance.toFixed(2)}
                    {r.is_low && <AlertTriangle size={10} className="inline ml-1" />}
                  </td>
                  <td className="p-3 text-right font-medium">₹{r.mtd_spend.toFixed(2)}</td>
                  <td className="p-3 text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))]">{r.status}</td>
                  <td className="p-3 text-right">
                    <Link to={`/superadmin/wallets/${r.tenant_id}`} className="text-xs underline text-[hsl(var(--kb-muted))]">
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "ok" ? "text-green-700" : tone === "bad" ? "text-red-700" : tone === "muted" ? "text-[hsl(var(--kb-muted))]" : "";
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
      <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-display ${cls}`}>{value}</div>
    </div>
  );
}
